import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Compass, Upload, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { restartGuidedTour } from "@/lib/tour.functions";
import { getMyProfile, updateProfile, deleteMyAccount } from "@/lib/settings.functions";
import { supabase } from "@/integrations/supabase/client";
import { useTheme } from "@/lib/theme";

export const Route = createFileRoute("/_authenticated/settings/profile")({
  component: ProfileSettings,
});

const COMMON_TZ = [
  "UTC", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Toronto", "Europe/London", "Europe/Berlin", "Europe/Paris", "Europe/Madrid",
  "Asia/Singapore", "Asia/Tokyo", "Asia/Kolkata", "Australia/Sydney",
];

function ProfileSettings() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fetchProfile = useServerFn(getMyProfile);
  const saveProfile = useServerFn(updateProfile);
  const deleteSelf = useServerFn(deleteMyAccount);
  const restart = useServerFn(restartGuidedTour);
  const { setTheme } = useTheme();

  const { data, isLoading } = useQuery({
    queryKey: ["my-profile"],
    queryFn: () => fetchProfile(),
  });
  const profile = data?.profile;

  const [fullName, setFullName] = useState("");
  const [tz, setTz] = useState("UTC");
  const [theme, setThemeLocal] = useState<"light" | "dark" | "system">("system");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Email change
  const [newEmail, setNewEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  // Password change
  const [newPwd, setNewPwd] = useState("");
  const [pwdLoading, setPwdLoading] = useState(false);
  // Delete
  const [confirmDelete, setConfirmDelete] = useState("");

  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name ?? "");
      setTz(profile.timezone ?? "UTC");
      setThemeLocal((profile.theme as any) ?? "system");
      setAvatarUrl(profile.avatar_url ?? null);
    }
  }, [profile]);

  const initials = (profile?.full_name || profile?.email || "?")
    .split(/\s+/).map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveProfile({
        data: { full_name: fullName, timezone: tz, theme, avatar_url: avatarUrl },
      });
      setTheme(theme);
      toast.success("Profile updated");
      await queryClient.invalidateQueries({ queryKey: ["my-profile"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (file: File) => {
    if (!profile) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    setUploading(true);
    try {
      const ext = file.name.split(".").pop() ?? "png";
      const path = `${profile.id}/avatar-${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from("avatars").upload(path, file, {
        upsert: true, contentType: file.type,
      });
      if (error) throw error;
      const { data: pub } = supabase.storage.from("avatars").getPublicUrl(path);
      setAvatarUrl(pub.publicUrl);
      await saveProfile({ data: { avatar_url: pub.publicUrl } });
      await queryClient.invalidateQueries({ queryKey: ["my-profile"] });
      toast.success("Avatar updated");
    } catch (e: any) {
      toast.error(e?.message ?? "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleEmailChange = async () => {
    if (!newEmail) return;
    setEmailLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ email: newEmail });
      if (error) throw error;
      toast.success("Verification email sent to your new address");
      setNewEmail("");
    } catch (e: any) {
      toast.error(e?.message ?? "Failed");
    } finally {
      setEmailLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (newPwd.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setPwdLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPwd });
      if (error) throw error;
      toast.success("Password updated");
      setNewPwd("");
    } catch (e: any) {
      toast.error(e?.message ?? "Failed");
    } finally {
      setPwdLoading(false);
    }
  };

  const handleRestart = async () => {
    try {
      await restart();
      await queryClient.invalidateQueries({ queryKey: ["tour-state"] });
      navigate({ to: "/command-center" });
    } catch (e: any) {
      toast.error(e?.message ?? "Couldn't restart tour");
    }
  };

  const handleDeleteSelf = async () => {
    try {
      await deleteSelf({ data: { confirm_email: confirmDelete } });
      await supabase.auth.signOut();
      navigate({ to: "/login" });
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to delete");
    }
  };

  if (isLoading || !profile) {
    return <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="space-y-6">
      {/* Profile section */}
      <section className="rounded-xl border border-border bg-card p-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-medium">Profile</h2>
            <p className="mt-1 text-sm text-muted-foreground">Your name, photo, and preferences.</p>
          </div>
          <Button variant="outline" size="sm" onClick={handleRestart}>
            <Compass className="mr-2 h-4 w-4" /> Guided Tour
          </Button>
        </div>

        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16">
            <AvatarImage src={avatarUrl ?? undefined} />
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); }}
            />
            <Button variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()}>
              {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              {uploading ? "Uploading…" : "Change avatar"}
            </Button>
            <p className="mt-1 text-xs text-muted-foreground">PNG/JPG up to 5MB</p>
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="full-name">Full name</Label>
            <Input id="full-name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="tz">Timezone</Label>
            <Select value={tz} onValueChange={setTz}>
              <SelectTrigger id="tz"><SelectValue /></SelectTrigger>
              <SelectContent>
                {COMMON_TZ.map((z) => (<SelectItem key={z} value={z}>{z}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="theme">Theme</Label>
            <Select value={theme} onValueChange={(v) => setThemeLocal(v as any)}>
              <SelectTrigger id="theme"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="light">Light</SelectItem>
                <SelectItem value="dark">Dark</SelectItem>
                <SelectItem value="system">System</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-6">
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save changes
          </Button>
        </div>
      </section>

      {/* Email + password */}
      <section className="rounded-xl border border-border bg-card p-6">
        <h2 className="text-base font-medium">Sign-in</h2>
        <p className="mt-1 text-sm text-muted-foreground">Current email: {profile.email}</p>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="new-email">New email</Label>
            <div className="flex gap-2">
              <Input id="new-email" type="email" placeholder="new@example.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
              <Button variant="outline" onClick={handleEmailChange} disabled={emailLoading || !newEmail}>
                {emailLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Change
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">We'll send a verification link to the new address.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-pwd">New password</Label>
            <div className="flex gap-2">
              <Input id="new-pwd" type="password" placeholder="At least 8 chars" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} />
              <Button variant="outline" onClick={handlePasswordChange} disabled={pwdLoading || !newPwd}>
                {pwdLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Update
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Danger */}
      <section className="rounded-xl border border-destructive/30 bg-card p-6">
        <h2 className="text-base font-medium text-destructive">Delete your account</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Permanently delete your user. If you're the sole owner of an account, transfer ownership first or use the account-wide delete on the Account tab.
        </p>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" className="mt-4" size="sm">Delete my user</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>This cannot be undone</AlertDialogTitle>
              <AlertDialogDescription>
                Type <span className="font-mono">{profile.email}</span> to confirm.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Input value={confirmDelete} onChange={(e) => setConfirmDelete(e.target.value)} placeholder={profile.email} />
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteSelf}
                disabled={confirmDelete.toLowerCase() !== profile.email.toLowerCase()}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete forever
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </section>
    </div>
  );
}
